import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import RealTimeStatusBadge from '../../components/ui/RealTimeStatusBadge';
import MapFilters from './components/MapFilters';
import InteractiveMap from './components/InteractiveMap';
import IncidentSidebar from './components/IncidentSidebar';
import IncidentPopup from './components/IncidentPopup';
import MapLegend from './components/MapLegend';

const GeographicOutageMap = () => {
  const [filters, setFilters] = useState({
    serviceTypes: [],
    timeRange: '24h',
    severity: [],
    status: 'all',
    location: 'all'
  });
  
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [showPopup, setShowPopup] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isLegendVisible, setIsLegendVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Mock incidents data
  const mockIncidents = [
    {
      id: 1,
      lat: 40.7128,
      lng: -74.0060,
      severity: 'critical',
      serviceType: 'internet',
      provider: 'Verizon FiOS',
      affectedUsers: 15420,
      duration: '2h 15m',
      status: 'active',
      title: 'Major Internet Outage in NYC',
      description: 'Widespread fiber network disruption affecting Manhattan and Brooklyn areas'
    },
    {
      id: 2,
      lat: 34.0522,
      lng: -118.2437,
      severity: 'major',
      serviceType: 'mobile',
      provider: 'T-Mobile',
      affectedUsers: 8930,
      duration: '45m',
      status: 'active',
      title: 'Mobile Service Disruption',
      description: 'Cell tower maintenance causing service interruptions in Los Angeles'
    },
    {
      id: 3,
      lat: 41.8781,
      lng: -87.6298,
      severity: 'minor',
      serviceType: 'streaming',
      provider: 'Netflix',
      affectedUsers: 2150,
      duration: '20m',
      status: 'active',
      title: 'Streaming Quality Issues',
      description: 'Users reporting buffering and connection timeouts in Chicago area'
    },
    {
      id: 4,
      lat: 47.6062,
      lng: -122.3321,
      severity: 'resolved',
      serviceType: 'cloud',
      provider: 'AWS',
      affectedUsers: 12500,
      duration: '1h 30m',
      status: 'resolved',
      title: 'AWS Service Restored',
      description: 'EC2 instances in us-west-2 region fully operational'
    },
    {
      id: 5,
      lat: 29.7604,
      lng: -95.3698,
      severity: 'major',
      serviceType: 'financial',
      provider: 'Chase Bank',
      affectedUsers: 5670,
      duration: '1h 5m',
      status: 'active',
      title: 'Online Banking Issues',
      description: 'Mobile app and website login problems reported in Houston'
    },
    {
      id: 6,
      lat: 33.4484,
      lng: -112.0740,
      severity: 'critical',
      serviceType: 'internet',
      provider: 'Cox Communications',
      affectedUsers: 18200,
      duration: '3h 20m',
      status: 'active',
      title: 'Cable Internet Outage',
      description: 'Infrastructure damage affecting Phoenix metropolitan area'
    }
  ];

  // Check if mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setIsSidebarVisible(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Simulate real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdate(new Date());
      // Simulate occasional connection issues
      if (Math.random() < 0.05) {
        setConnectionStatus('connecting');
        setTimeout(() => setConnectionStatus('connected'), 2000);
      }
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleMarkerClick = (incident, event) => {
    if (event) {
      setPopupPosition({ x: event?.clientX, y: event?.clientY });
    }
    setSelectedIncident(incident);
    setShowPopup(true);
  };

  const handleIncidentSelect = (incident) => {
    setSelectedIncident(incident);
    if (isMobile) {
      setIsSidebarVisible(false);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
    setSelectedIncident(null);
  };

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  const toggleLegend = () => {
    setIsLegendVisible(!isLegendVisible);
  };

  const filteredIncidents = mockIncidents?.filter(incident => {
    if (filters?.serviceTypes?.length > 0 && !filters?.serviceTypes?.includes(incident?.serviceType)) {
      return false;
    }
    if (filters?.severity?.length > 0 && !filters?.severity?.includes(incident?.severity)) {
      return false;
    }
    if (filters?.status !== 'all' && incident?.status !== filters?.status) {
      return false;
    }
    return true;
  });

  const getActiveIncidentCount = () => {
    return filteredIncidents?.filter(incident => incident?.status === 'active')?.length;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16 h-screen flex flex-col">
        {/* Page Header */}
        <div className="bg-card border-b border-border px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
                  <Icon name="Map" size={24} color="white" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-foreground">
                    Geographic Outage Map
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    Real-time visualization of service disruptions
                  </p>
                </div>
              </div>
              
              {/* Quick Stats */}
              <div className="hidden lg:flex items-center space-x-6 ml-8">
                <div className="text-center">
                  <p className="text-lg font-semibold text-error">
                    {getActiveIncidentCount()}
                  </p>
                  <p className="text-xs text-muted-foreground">Active</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold text-foreground">
                    {filteredIncidents?.length}
                  </p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold text-warning">
                    {filteredIncidents?.reduce((sum, incident) => sum + incident?.affectedUsers, 0)?.toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground">Users Affected</p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <RealTimeStatusBadge 
                connectionStatus={connectionStatus}
                lastUpdate={lastUpdate}
              />
              
              {/* Mobile Controls */}
              {isMobile && (
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleSidebar}
                  >
                    <Icon name="List" size={16} />
                  </Button>
                </div>
              )}

              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center space-x-2">
                <Button variant="outline" size="sm" asChild>
                  <Link to="/outage-dashboard">
                    <Icon name="BarChart3" size={16} className="mr-2" />
                    Dashboard
                  </Link>
                </Button>
                <Button variant="default" size="sm" asChild>
                  <Link to="/report-outage">
                    <Icon name="Plus" size={16} className="mr-2" />
                    Report Issue
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <MapFilters 
          onFiltersChange={handleFiltersChange}
          isMobile={isMobile}
        />

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Sidebar */}
          {(!isMobile || isSidebarVisible) && (
            <IncidentSidebar
              incidents={filteredIncidents}
              selectedIncident={selectedIncident}
              onIncidentSelect={handleIncidentSelect}
              isVisible={isSidebarVisible}
              onToggle={toggleSidebar}
              isMobile={isMobile}
            />
          )}

          {/* Map Container */}
          <div className="flex-1 relative">
            <InteractiveMap
              filters={filters}
              onMarkerClick={handleMarkerClick}
              selectedIncident={selectedIncident}
            />

            {/* Map Legend */}
            <MapLegend
              isVisible={isLegendVisible}
              onToggle={toggleLegend}
            />

            {/* Mobile Floating Action Button */}
            {isMobile && (
              <div className="absolute bottom-20 right-4 flex flex-col space-y-2">
                <Button
                  variant="default"
                  size="icon"
                  className="rounded-full shadow-lg"
                  asChild
                >
                  <Link to="/report-outage">
                    <Icon name="Plus" size={20} />
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Incident Popup */}
        {showPopup && selectedIncident && (
          <IncidentPopup
            incident={selectedIncident}
            onClose={closePopup}
            position={popupPosition}
          />
        )}

        {/* Mobile Bottom Navigation */}
        {isMobile && (
          <div className="bg-card border-t border-border p-4">
            <div className="flex items-center justify-around">
              <Link
                to="/outage-dashboard"
                className="flex flex-col items-center space-y-1 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="BarChart3" size={20} />
                <span className="text-xs">Dashboard</span>
              </Link>
              
              <button
                onClick={toggleSidebar}
                className="flex flex-col items-center space-y-1 text-foreground"
              >
                <Icon name="Map" size={20} />
                <span className="text-xs">Map</span>
              </button>
              
              <Link
                to="/report-outage"
                className="flex flex-col items-center space-y-1 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="Plus" size={20} />
                <span className="text-xs">Report</span>
              </Link>
              
              <Link
                to="/service-outage-details"
                className="flex flex-col items-center space-y-1 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="FileText" size={20} />
                <span className="text-xs">Details</span>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeographicOutageMap;